import { useLanderConfig } from "../commons/ConfigContext";
import {
  isRollerAdsGeoOverride,
  isYahooApprovedMarket,
} from "../commons/HelperFunctions";
import { FeedType, LanderConfig } from "../types/LanderConfig";
import { buildRsocRedirectUrl } from "../rsoc/useRsoc";
import { useRollerAdsUsExperiment } from "../experimentation/useRollerAdsUsExperiment";

export enum AdProvider {
  RSOC = "rsoc",
  ROLLER_ADS = "roller_ads",
  YAHOO = "yahoo",
}

export interface AdProviderResult {
  provider: AdProvider;
  rsocUrl: string | null;
  rsocError: boolean;
  experimentKeywordClick: boolean;
  yahooUnapprovedKeywordClick: boolean;
}

function isRsocEligible(landerConfig: LanderConfig): boolean {
  const rsocPathOrUrlExists =
    !!landerConfig.rsoc?.path || !!landerConfig.rsoc?.url;
  const feedIDIsRsoc = landerConfig.lander?.feedID === FeedType.RSOC;
  const domainIsCloudynestStore =
    landerConfig.domain?.rootDomain === "cloudynest.store";

  return rsocPathOrUrlExists && feedIDIsRsoc && !domainIsCloudynestStore;
}

/**
 * Builds an AdProviderResult, defaulting every field the caller leaves out, so
 * each branch below is one statement naming only what it changes.
 */
function adProviderResult(
  provider: AdProvider,
  overrides: Partial<AdProviderResult> = {}
): AdProviderResult {
  return {
    provider,
    rsocUrl: null,
    rsocError: false,
    experimentKeywordClick: false,
    yahooUnapprovedKeywordClick: false,
    ...overrides,
  };
}

/** RollerAds serving 1-click keyword links instead of the 0-click redirect. */
function rollerAdsKeywordClick(): AdProviderResult {
  return adProviderResult(AdProvider.ROLLER_ADS, {
    experimentKeywordClick: true,
  });
}

/** RSOC when its redirect URL builds, otherwise the Yahoo error fallback. */
function resolveRsoc(landerConfig: LanderConfig): AdProviderResult {
  const { url } = buildRsocRedirectUrl(landerConfig);
  return url
    ? adProviderResult(AdProvider.RSOC, { rsocUrl: url })
    : adProviderResult(AdProvider.YAHOO, { rsocError: true });
}

/**
 * Resolves which ad provider to use. Priority order:
 * 1. Geo-override (CN + the keyword-click geos) -> ROLLER_ADS
 * 2. RSOC-eligible: treatment overrides to ROLLER_ADS 1-click; otherwise RSOC
 * 3. feedID === ROLLER_ADS_1CLICK -> ROLLER_ADS with experimentKeywordClick
 * 4. feedID === ROLLER_ADS -> ROLLER_ADS (unaffected by experiment)
 * 5. US experiment treatment -> ROLLER_ADS 1-click
 * 6. Market Yahoo is not approved to monetize -> ROLLER_ADS 1-click
 * 7. Yahoo baseline -> YAHOO
 */
export function useAdProvider(): AdProviderResult {
  const { landerConfig } = useLanderConfig();
  const { inRollerAdsTreatment } = useRollerAdsUsExperiment();
  const feedID = landerConfig.lander?.feedID;

  if (isRollerAdsGeoOverride()) return adProviderResult(AdProvider.ROLLER_ADS);

  if (isRsocEligible(landerConfig)) {
    return inRollerAdsTreatment
      ? rollerAdsKeywordClick()
      : resolveRsoc(landerConfig);
  }

  if (feedID === FeedType.ROLLER_ADS_1CLICK) return rollerAdsKeywordClick();
  if (feedID === FeedType.ROLLER_ADS) {
    return adProviderResult(AdProvider.ROLLER_ADS);
  }
  if (inRollerAdsTreatment) return rollerAdsKeywordClick();
  if (!isYahooApprovedMarket()) {
    return adProviderResult(AdProvider.ROLLER_ADS, {
      yahooUnapprovedKeywordClick: true,
    });
  }

  return adProviderResult(AdProvider.YAHOO);
}
